/**
 * AP57 — Ders picker dedup. `subjects` tablosunda aynı dersin hem İngilizce/
 * Türkçe çifti (Fizik id16 + Physics id2) hem birebir Türkçe mükerrerleri
 * (Matematik id12 + id1) var ve HER İKİSİ de kitap sahibi. Ders seçenekleri
 * kitaplardan türetildiği için aynı ders listede iki (Tüm Dersler öğretmeninde
 * dörde kadar) kez görünüyordu.
 *
 * Kanonik ada göre gruplar; temsilci id = gruptaki EN ÇOK kitabı olan subject
 * satırı (kazanım/konu listeleri o subject'in subject_units'inden yüklendiği
 * için en dolu satır seçilir). Kalıcı çözüm (satırları birleştirme) AP9'un işi.
 */

const EN_TO_TR: Record<string, string> = {
  physics: 'Fizik',
  chemistry: 'Kimya',
  biology: 'Biyoloji',
  mathematics: 'Matematik',
  math: 'Matematik',
  history: 'Tarih',
  geography: 'Coğrafya',
  turkish: 'Türkçe',
  literature: 'Edebiyat',
  english: 'İngilizce',
  philosophy: 'Felsefe',
  religion: 'Din Kültürü ve Ahlak Bilgisi',
  art: 'Görsel Sanatlar',
  music: 'Müzik',
  science: 'Fen Bilimleri',
  'turkish language & literature': 'Türk Dili ve Edebiyatı',
  'social studies': 'Sosyal Bilgiler',
};

const canonicalName = (raw: string): string => EN_TO_TR[raw.trim().toLowerCase()] ?? raw.trim();

// İnkılâp/İnkılap gibi şapkalı varyantları da aynı anahtara indir (admin
// panelin normaliseForDedup kuralıyla aynı).
const normKey = (s: string): string =>
  s
    .replace(/â/g, 'a').replace(/Â/g, 'A')
    .replace(/î/g, 'i').replace(/Î/g, 'I')
    .replace(/û/g, 'u').replace(/Û/g, 'U')
    .trim()
    .toLocaleLowerCase('tr');

/**
 * One key per ders for a plain name list (Kütüphanem's filter): Fizik / Physics /
 * "fizik " / İnkılâp / İnkılap each fold to a single key, so a dropdown built from
 * book subject names lists a ders once. `canonicalSubjectName` is what to show for it.
 */
export const subjectGroupKey = (raw: string): string => normKey(canonicalName(raw));
export const canonicalSubjectName = (raw: string): string => canonicalName(raw);

export function uniqueSubjectChoices(names: Array<string | null | undefined>): Array<{ key: string; label: string }> {
  const seen = new Map<string, string>();
  for (const raw of names) {
    if (!raw || !raw.trim()) continue;
    const key = subjectGroupKey(raw);
    if (!seen.has(key)) seen.set(key, canonicalName(raw));
  }
  return Array.from(seen, ([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
}

export interface BookLike {
  subjectId?: number | null;
  subject?: { id?: number | null; name?: string | null } | null;
}

export function dedupSubjectOptionsFromBooks(books: BookLike[]): Array<{ id: number; name: string }> {
  const groups = new Map<string, { name: string; counts: Map<number, number> }>();
  for (const b of books) {
    const sid = b.subjectId ?? b.subject?.id;
    const rawName = b.subject?.name;
    if (!sid || !rawName) continue;
    // "Tüm Dersler" wildcard satırı ve test verisi dersleri picker'a girmesin.
    if (/^t[uü]m\s+dersler$/i.test(rawName.trim()) || rawName.trim() === 'Test Ders') continue;
    const display = canonicalName(rawName);
    const key = normKey(display);
    const g = groups.get(key) ?? { name: display, counts: new Map<number, number>() };
    // Gruptaki gerçek Türkçe ad, çeviriden türetilmiş ada tercih edilir.
    if (rawName.trim() === display) g.name = display;
    g.counts.set(Number(sid), (g.counts.get(Number(sid)) ?? 0) + 1);
    groups.set(key, g);
  }
  return Array.from(groups.values())
    .map((g) => {
      let bestId = 0;
      let bestCount = -1;
      g.counts.forEach((count, id) => {
        if (count > bestCount) {
          bestId = id;
          bestCount = count;
        }
      });
      return { id: bestId, name: g.name };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
}
